import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Globe, LogOut } from "lucide-react";
import { useLang, LANGS } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import logo from "../assets/edvia-logo.png";

const linkCls = ({ isActive }) =>
  `transition-colors hover:text-foreground ${isActive ? "font-semibold text-primary border-b-2 border-primary pb-0.5" : "text-muted-foreground"}`;

export function Navbar({ settings }) {
  const { t, lang, setLang } = useLang();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const siteName = settings?.siteName || "EdviaIQ";

  return (
    <nav className="sticky top-0 z-40 border-b border-border bg-background/85 backdrop-blur-md" data-testid="navbar">
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between gap-3 px-4">
        <Link to="/" className="flex shrink-0 items-center gap-2" data-testid="nav-logo">
          <img src={logo} alt={siteName} className="h-9 w-auto object-contain" />
        </Link>

        <div className="hidden items-center gap-5 text-sm font-medium lg:flex">
          <NavLink to="/" end className={linkCls} data-testid="nav-home">{t("nav.home")}</NavLink>
          <NavLink to="/types" className={linkCls} data-testid="nav-types">{t("nav.archetypes")}</NavLink>
          <NavLink to="/compatibility" className={linkCls} data-testid="nav-compatibility">{t("nav.compatibility")}</NavLink>
          <NavLink to="/faq" className={linkCls} data-testid="nav-faq">{t("nav.faq")}</NavLink>
          {user && <NavLink to="/dashboard" className={linkCls} data-testid="nav-dashboard">{t("nav.dashboard")}</NavLink>}
          {user && <NavLink to="/profile" className={linkCls} data-testid="nav-profile">{t("nav.profile")}</NavLink>}
          {user?.role === "admin" && <NavLink to="/admin" className={linkCls} data-testid="nav-admin">{t("nav.admin")}</NavLink>}
        </div>

        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 rounded-full border border-border p-0.5" data-testid="language-toggle">
            <Globe className="ml-1.5 h-3.5 w-3.5 text-muted-foreground" />
            {LANGS.map((l) => (
              <button
                key={l.code}
                onClick={() => setLang(l.code)}
                className={`rounded-full px-2 py-1 text-xs font-semibold transition-colors ${
                  lang === l.code ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-primary"
                }`}
                data-testid={`lang-${l.code}`}
                aria-label={l.full}
              >
                {l.label}
              </button>
            ))}
          </div>
          {user ? (
            <>
              <span className="hidden max-w-[120px] truncate text-sm font-medium text-foreground sm:block" data-testid="nav-username">
                {user.name?.split(" ")[0]}
              </span>
              <button
                onClick={async () => { await logout(); navigate("/"); }}
                className="flex h-9 w-9 items-center justify-center rounded-full border border-border text-muted-foreground transition-colors hover:border-primary hover:text-primary"
                data-testid="nav-logout"
                aria-label={t("nav.logout")}
                title={t("nav.logout")}
              >
                <LogOut className="h-4 w-4" />
              </button>
            </>
          ) : (
            <>
              <Link to="/login" className="hidden sm:block">
                <Button variant="ghost" size="sm" data-testid="nav-login">{t("nav.login")}</Button>
              </Link>
              <Link to="/signup">
                <Button size="sm" data-testid="nav-signup">{t("nav.signup")}</Button>
              </Link>
            </>
          )}
        </div>
      </div>

      {/* mobile menu row */}
      <div className="flex items-center gap-4 overflow-x-auto border-t border-border px-4 py-2 text-xs font-medium lg:hidden" data-testid="mobile-menu">
        <NavLink to="/" end className={linkCls}>{t("nav.home")}</NavLink>
        <NavLink to="/types" className={linkCls}>{t("nav.archetypes")}</NavLink>
        <NavLink to="/compatibility" className={linkCls}>{t("nav.compatibility")}</NavLink>
        <NavLink to="/faq" className={linkCls}>{t("nav.faq")}</NavLink>
        {user && <NavLink to="/dashboard" className={linkCls}>{t("nav.dashboard")}</NavLink>}
        {user && <NavLink to="/profile" className={linkCls}>{t("nav.profile")}</NavLink>}
        {user?.role === "admin" && <NavLink to="/admin" className={linkCls}>{t("nav.admin")}</NavLink>}
      </div>
    </nav>
  );
}
